//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  ContentSourceAddProductsToCartRequest,
  ContentSourceCartSummaryResponse,
  ContentSourceCartTaskResponse,
  ContentSourceEligibleReviewsByProductRequest,
  ContentSourceEligibleReviewsByProductResponse,
  ContentSourceProductReviewMediaRequest,
  ContentSourceProductReviewMediaResponse,
  ContentSourceProductsRequest,
  ContentSourceProductsResponse,
  ContentSourceRemoveProductFromCartRequest,
  ContentSourceRemoveProductFromCartResponse,
  ContentSourceTaskDetailsRequest,
  ContentSourceTaskDetailsResponse,
} from '@apis/campaigns/contentSourceApiModel';
import { deserializeContentSourceProductReview } from '@apis/campaigns/contentSourceApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CONTENT_SOURCE_DEFAULT_ENDPOINT = '/api/v1/content-source';

const contentSourceSamplingGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${CONTENT_SOURCE_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );

/**
 * ContentSource products
 *
 * POST /api/v1/content-source/products
 */
export const findContentSourceProducts = async (
  req: ContentSourceProductsRequest,
  init?: RequestInit,
): Promise<ContentSourceProductsResponse> => {
  const res = await contentSourceSamplingGatewayFetch(`/products`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(req),
  });

  const contentSourceProductsResponse = await res.json<{
    data: ContentSourceProductsResponse;
  }>();

  return contentSourceProductsResponse.data;
};

/**
 * ContentSource product reviews
 *
 * GET /api/v1/content-source/products/:casProductId/reviews
 */
export const getContentSourceEligibleReviewsByProduct = async (
  req: ContentSourceEligibleReviewsByProductRequest,
  init?: RequestInit,
): Promise<ContentSourceEligibleReviewsByProductResponse> => {
  const { casProductId, ...queryParams } = req;

  const queryString = makeParams(queryParams);

  const res = await contentSourceSamplingGatewayFetch(
    `/products/${casProductId}/reviews?${queryString}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const contentSourceProductReviewsResponse = await res.json<{
    data: ContentSourceEligibleReviewsByProductResponse;
  }>((res) => ({
    data: {
      ...res.data,
      results: res.data.results?.map(deserializeContentSourceProductReview),
    },
  }));

  return contentSourceProductReviewsResponse.data;
};

/**
 * ContentSource product review media
 *
 * GET /api/v1/content-source/products/:casProductId/media
 */
export const getContentSourceProductReviewMedia = async (
  req: ContentSourceProductReviewMediaRequest,
  init?: RequestInit,
): Promise<ContentSourceProductReviewMediaResponse> => {
  const { casProductId, ...queryParams } = req;

  const queryString = makeParams(queryParams);

  const res = await contentSourceSamplingGatewayFetch(
    `/products/${casProductId}/media?${queryString}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const contentSourceProductReviewMediaResponse = await res.json<{
    data: ContentSourceProductReviewMediaResponse;
  }>();

  return contentSourceProductReviewMediaResponse.data;
};

/**
 * ContentSource cart summary
 *
 * GET /api/v1/content-source/cart/summary
 */
export const getContentSourceCartSummary = async (
  init?: RequestInit,
): Promise<ContentSourceCartSummaryResponse> => {
  const res = await contentSourceSamplingGatewayFetch(`/cart/summary/`, {
    ...init,
    method: 'GET',
  });

  return await res.json<ContentSourceCartSummaryResponse>();
};

/**
 * ContentSource remove product from cart
 *
 * DELETE /api/v1/content-source/cart/items/:casProductId
 */
export const removeContentSourceProductFromCart = async (
  req: ContentSourceRemoveProductFromCartRequest,
  init?: RequestInit,
): Promise<ContentSourceRemoveProductFromCartResponse> => {
  const { casProductId } = req;

  const res = await contentSourceSamplingGatewayFetch(
    `/cart/items/${casProductId}`,
    {
      ...init,
      method: 'DELETE',
    },
  );

  const removeProductFromCartResponse = await res.json<{
    data: ContentSourceRemoveProductFromCartResponse;
  }>();

  return removeProductFromCartResponse.data;
};

/**
 * ContentSource removes all products from the cart
 *
 * DELETE /api/v1/content-source/cart/items
 */
export const removeAllContentSourceProductsFromCart = async (
  init?: RequestInit,
): Promise<ContentSourceRemoveProductFromCartResponse> => {
  const res = await contentSourceSamplingGatewayFetch(`/cart/items`, {
    ...init,
    method: 'DELETE',
  });

  const removeProductFromCartResponse = await res.json<{
    data: ContentSourceRemoveProductFromCartResponse;
  }>();

  return removeProductFromCartResponse.data;
};

/**
 * ContentSource task details
 *
 * GET /api/v1/content-source/tasks/:taskUid
 */
export const getContentSourceTaskDetails = async (
  req: ContentSourceTaskDetailsRequest,
  init?: RequestInit,
): Promise<ContentSourceTaskDetailsResponse> => {
  const { taskUid } = req;

  const res = await contentSourceSamplingGatewayFetch(`/tasks/${taskUid}`, {
    ...init,
    method: 'GET',
  });

  const taskDetailsResponse = await res.json<{
    data: ContentSourceTaskDetailsResponse;
  }>();

  return taskDetailsResponse.data;
};

/**
 * ContentSource add products to cart
 *
 * POST /api/v1/content-source/cart/items
 */
export const addContentSourceProductsToCart = async (
  req: ContentSourceAddProductsToCartRequest,
  init?: RequestInit,
): Promise<ContentSourceCartTaskResponse> => {
  const res = await contentSourceSamplingGatewayFetch(`/cart/items`, {
    ...init,
    method: 'POST',
    body: JSON.stringify(req),
  });

  const addProductToCartResponse = await res.json<{
    data: ContentSourceCartTaskResponse;
  }>();

  return addProductToCartResponse.data;
};

/**
 * ContentSource submit order
 *
 * POST /api/v1/content-source/submit-order
 */
export const submitContentSourceOrder = async (
  init?: RequestInit,
): Promise<ContentSourceCartTaskResponse> => {
  const res = await contentSourceSamplingGatewayFetch(`/submit-order`, {
    ...init,
    method: 'POST',
  });

  const submitOrderResponse = await res.json<{
    data: ContentSourceCartTaskResponse;
  }>();

  return submitOrderResponse.data;
};
